/**
 * IdentityCenter — Stage 3.5 (2026-02).
 *
 * Art-direction pass: the person must feel like they are **emerging
 * from Northstar**, not sitting inside an avatar frame. Concretely:
 *
 *   - No hard circular border. No visible ring frame around the
 *     photograph. The digital ring system already sits behind the
 *     person; adding a border here would double-encapsulate them.
 *   - A generous radial `mask-image` fades the edges of the
 *     photograph so shoulders/head dissolve into the atmosphere.
 *   - An emerald edge-tint layer bleeds green into the periphery of
 *     the image — hiding the stock photograph's original background
 *     without requiring server-side background removal, so the
 *     figure feels wrapped in Northstar's illumination.
 *   - A soft warm ambient halo extends BEHIND the portrait (no
 *     symmetric ring in front of the shoulders).
 *   - Real member photographs shipped through the S3 pipeline get
 *     the same treatment — production code path is unchanged.
 */
import React from "react";

/** Editorial stock human — dev/reference only; real photos override. */
const STOCK_PORTRAIT_URL =
  "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=800&h=1000&fit=crop&crop=faces&auto=format&q=85";

export default function IdentityCenter({
  profile,
  size = 300,
  haloExtend = 42,
}) {
  const initial =
    profile?.initial ||
    (profile?.name || profile?.email || "?").charAt(0).toUpperCase();
  const memberPhoto = profile?.photo_url;
  const photoSrc = memberPhoto || STOCK_PORTRAIT_URL;

  return (
    <div
      className="relative"
      style={{ width: size, height: size }}
      data-testid="northstar-identity-center"
      data-portrait-size={size}
    >
      {/* Warm ambient halo — extends generously behind the portrait.
          Deliberately offset upward-right so the light source reads
          as organic, not a symmetric disc. */}
      <div
        aria-hidden="true"
        className="absolute rounded-full blur-3xl pointer-events-none"
        style={{
          inset: `-${haloExtend * 2}px`,
          background:
            "radial-gradient(ellipse 62% 50% at 46% 42%, rgba(126,212,147,0.42) 0%, rgba(48,148,102,0.20) 44%, rgba(126,212,147,0) 76%)",
        }}
      />

      {/* Portrait — no hard border. A tall head+shoulders mask fades
          the edges into the atmosphere; multiple green overlays
          suppress the visible rectangular photograph source
          (particularly the sky/horizon behind the head) while
          preserving natural skin tone at the face. */}
      <div
        className="relative w-full h-full"
        style={{
          WebkitMaskImage:
            "radial-gradient(ellipse 52% 72% at 50% 46%, rgba(0,0,0,1) 34%, rgba(0,0,0,0.98) 58%, rgba(0,0,0,0.78) 72%, rgba(0,0,0,0.42) 84%, rgba(0,0,0,0.14) 94%, rgba(0,0,0,0) 100%)",
          maskImage:
            "radial-gradient(ellipse 52% 72% at 50% 46%, rgba(0,0,0,1) 34%, rgba(0,0,0,0.98) 58%, rgba(0,0,0,0.78) 72%, rgba(0,0,0,0.42) 84%, rgba(0,0,0,0.14) 94%, rgba(0,0,0,0) 100%)",
        }}
        data-testid={memberPhoto ? "identity-photo" : "identity-stock"}
      >
        <img
          src={photoSrc}
          alt={profile?.name ? `${profile.name}'s portrait` : "Member portrait"}
          className="w-full h-full object-cover"
          style={{ objectPosition: "50% 34%" }}
          onError={(e) => {
            e.currentTarget.style.display = "none";
            const fallback = e.currentTarget.parentElement?.parentElement
              ?.querySelector("[data-role=initial-fallback]");
            if (fallback) fallback.style.display = "flex";
          }}
        />

        {/* Top + bottom emerald wash — the top band is fully opaque
            dark green (via multiply) to obliterate what little sky
            remains above the head. Transparent band across the
            face preserves natural skin tone. The bottom band fades
            the below-shoulders slab into atmospheric green. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(to bottom, rgba(4,22,14,1.0) 0%, rgba(4,22,14,0.94) 12%, rgba(6,32,22,0.42) 22%, rgba(6,32,22,0) 32%, rgba(6,32,22,0) 60%, rgba(6,32,22,0.32) 74%, rgba(6,32,22,0.78) 88%, rgba(4,22,14,1.0) 100%)",
            mixBlendMode: "multiply",
          }}
        />

        {/* Emerald edge-tint — restricted to the outer edge band so
            the face and upper body keep their natural skin tone. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 44% 62% at 50% 44%, rgba(6,44,32,0) 62%, rgba(10,54,38,0.32) 78%, rgba(6,32,22,0.85) 96%)",
            mixBlendMode: "multiply",
          }}
        />

        {/* Mint rim light — subtle emerald reflection at the outer
            periphery; feels like environmental illumination. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 44% 62% at 50% 44%, rgba(126,212,147,0) 70%, rgba(126,212,147,0.10) 84%, rgba(126,212,147,0.22) 96%)",
            mixBlendMode: "screen",
          }}
        />
      </div>

      {/* Initial fallback — hidden unless the image errors out. */}
      <div
        data-role="initial-fallback"
        className="absolute inset-0 hidden items-center justify-center text-white font-extrabold"
        style={{ fontSize: size * 0.44 }}
      >
        <span>{initial}</span>
      </div>
    </div>
  );
}
